import { useState, useEffect } from "react";
import RichTextEditor from "react-rte";
import { useParams } from "react-router-dom";
import newService from "../../../services/newService";
import imageUpload from "../../../services/upload";
import {
  Select,
  MenuItem,
  FormControl,
  TextField,
  FormHelperText,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { IMAGE_PATH, instanceToken } from "../../../utils/constant";
import { createNewPostSchema } from "../../../schema/createNewPostSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import {
  EditPageLayout,
  EditImageSection,
  EditFieldLabel,
  EditFormTopSection,
  EditFormParagraphsSection,
} from "../../../components/edit/EditPageLayout";

const imgFileTypes = ["image/gif", "image/jpeg", "image/png", "image/svg+xml"];

export default function UpdateNews({ homeAlert }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [titleEng, setTitleEng] = useState("");
  const [titleMM, setTitleMM] = useState("");
  const [category, setCategory] = useState("");
  const [parEng, setParEng] = useState(RichTextEditor.createEmptyValue());
  const [parMM, setParMM] = useState(RichTextEditor.createEmptyValue());
  const [imagePreview, setImagePreview] = useState("");
  const [imageFile, setImageFile] = useState("");
  const [imageFileUrl, setImageFileUrl] = useState("");
  const [values, setValues] = useState({ image_url: "" });
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState({});

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(createNewPostSchema),
    initialValues: {
      title_en: "",
      title_mm: "",
      paragraph_en: "<p></p>",
      paragraph_mm: "<p></p>",
      category: "",
    },
  });

  async function getImageUrl(imgData) {
    const response = await imageUpload.getImageUrl(
      instanceToken.token,
      imgData
    );
    const result = response.data;
    const path = IMAGE_PATH + result?.imageName;
    setImageFileUrl(result?.imageUploadUrl);
    setValues({
      ...values,
      image_url: path,
    });
  }

  useEffect(() => {
    if (!news) {
      fetchNews();
    }
  }, []);

  useEffect(() => {
    setTitleEng(news?.title_en);
    setTitleMM(news?.title_mm);
    setCategory(news?.category);
    setParEng(RichTextEditor.createValueFromString(news?.paragraph_en, "html"));
    setParMM(RichTextEditor.createValueFromString(news?.paragraph_mm, "html"));

    if (news) {
      setValue("title_en", news?.title_en);
      setValue("title_mm", news?.title_mm);
      setValue("paragraph_en", news?.paragraph_en);
      setValue("paragraph_mm", news?.paragraph_mm);
      setValue("category", news?.category);

      setValues({
        image_url: news?.image_url || "",
      });
    }
  }, [news]);

  const fetchNews = async () => {
    try {
      const res = await newService.getOneNews(instanceToken.token, id);
      setNews(res.data);
    } catch (error) {
      console.error(error);
      throw new Error(error);
    }
  };

  const changeTitleMM = (e) => {
    setTitleMM(e.target.value.trim());
  };
  const changeTitleEng = (e) => {
    setTitleEng(e.target.value.trim());
  };

  const changeCategory = (e) => {
    setCategory(e.target.value);
  };

  const changeFile = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const img = e.target.files[0];

      if (!imgFileTypes.includes(img.type)) {
        setErrorMessage({
          ...errorMessage,
          image_url: "Please select image. (PNG, JPG, JPEG, GIF, ...)",
        });
        return;
      }
      if (img.size > 204800) {
        setErrorMessage({
          ...errorMessage,
          image_url: "Image file size must be smaller than 200KB.",
        });
        return;
      }
      setImageFile(img);
      setImagePreview(URL.createObjectURL(img));
      getImageUrl(img);
    }
  };

  const formData = new FormData();

  const handleUpdate = async (data) => {
    formData.append("title_en", data?.title_en);
    formData.append("title_mm", data?.title_mm);
    formData.append("paragraph_en", data?.paragraph_en);
    formData.append("paragraph_mm", data?.paragraph_mm);
    formData.append("category", category ? category : data?.category);
    formData.append(
      "image_url",
      values?.image_url ? values?.image_url : data?.image_url
    );
    formData.append("image_data", "");

    try {
      setLoading(true);
      if (imageFile) {
        await imageUpload.uploadImage(imageFileUrl, imageFile);
      }
      const response = await newService.putNews(
        instanceToken.token,
        news?.id,
        formData
      );
      navigate("/all_news");
      setNews(response.data);
      homeAlert("News Post have been Updated.", false);
    } catch (error) {
      console.log(error);
      homeAlert("Error on server.", true);
    } finally {
      setLoading(false);
      setErrorMessage({});
    }
  };

  const handleRemove = async () => {
    try {
      await newService.deleteNews(instanceToken.token, id);
      homeAlert("New post have been removed.", false);
      navigate("/all_news");
    } catch (error) {
      console.log(error);
      homeAlert("Error on server!", true);
    }
  };

  return (
    <EditPageLayout
      title="Edit News Post"
      subtitle="Update your news post details and content in both languages."
      breadcrumbs={[
        { label: "News", path: "/all_news" },
        { label: `News (ID - ${id})`, path: `/news/${id}` },
        { label: "Edit" },
      ]}
      detailPath={`/news/${id}`}
      onRemove={handleRemove}
      loading={loading}
      onSubmit={handleSubmit(handleUpdate)}
      removeMessage="Are you sure you want to remove this news post?"
    >
      <EditFormTopSection
        image={
          <EditImageSection
            uploadLabel="News Image"
            previewLabel="Image Preview"
            imageUrl={values?.image_url}
            imagePreview={imagePreview}
            onFileChange={changeFile}
            error={errorMessage["image_url"]}
            recommendedSize="1920 x 1080 px (16:9)"
          />
        }
      >
        <div>
          <EditFieldLabel required>Title (English)</EditFieldLabel>
          <TextField
            fullWidth
            id="title_en"
            {...register("title_en")}
            onChange={changeTitleEng}
            value={titleEng}
            error={!!errors.title_en}
            helperText={errors.title_en?.message}
          />
        </div>
        <div>
          <EditFieldLabel required>Title (Myanmar)</EditFieldLabel>
          <TextField
            fullWidth
            id="title_mm"
            {...register("title_mm")}
            onChange={changeTitleMM}
            value={titleMM}
            error={!!errors.title_mm}
            helperText={errors.title_mm?.message}
          />
        </div>
        <div>
          <EditFieldLabel required>Category</EditFieldLabel>
          <FormControl fullWidth error={!!errors.category}>
            <Select value={category} onChange={changeCategory} displayEmpty>
              <MenuItem value="" disabled>
                Select category
              </MenuItem>
              <MenuItem value="News">News</MenuItem>
              <MenuItem value="Event">Event</MenuItem>
              <MenuItem value="Promotion">Promotion</MenuItem>
            </Select>
            <FormHelperText error>
              {errors.category?.message || errorMessage["category"]}
            </FormHelperText>
          </FormControl>
        </div>
      </EditFormTopSection>

      <EditFormParagraphsSection>
        <div className="edit-editor-field">
          <EditFieldLabel required>Paragraph (English)</EditFieldLabel>
          <Controller
            name="paragraph_en"
            control={control}
            render={() => (
              <>
                <RichTextEditor
                  value={parEng}
                  onChange={(value) => {
                    setParEng(value);
                    setValue(
                      "paragraph_en",
                      value
                        .toString("html")
                        .replace(/<[^>]*>/g, "")
                        .trim()
                    );
                  }}
                />
                {errors.paragraph_en && (
                  <FormHelperText error>
                    {errors.paragraph_en.message}
                  </FormHelperText>
                )}
              </>
            )}
          />
        </div>

        <div className="edit-editor-field">
          <EditFieldLabel required>Paragraph (Myanmar)</EditFieldLabel>
          <Controller
            name="paragraph_mm"
            control={control}
            render={() => (
              <>
                <RichTextEditor
                  value={parMM}
                  onChange={(value) => {
                    setParMM(value);
                    setValue(
                      "paragraph_mm",
                      value
                        .toString("html")
                        .replace(/<[^>]*>/g, "")
                        .trim()
                    );
                  }}
                />
                {errors.paragraph_mm && (
                  <FormHelperText error>
                    {errors.paragraph_mm.message}
                  </FormHelperText>
                )}
              </>
            )}
          />
        </div>
      </EditFormParagraphsSection>
    </EditPageLayout>
  );
}
